import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { companyApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  CardActions,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  LinkButton,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  Select,
  statusLabel,
  useToast,
} from '@/components/ui';
import { MemberPermissionsModal } from '@/features/access/MemberPermissionsModal';
import { COMPANY_ROLE_OPTIONS as ROLES, roleLabel } from '@/features/access/roles';
import { applyFieldErrors, errorMessage, isPlanLimitError } from '@/hooks/useApiError';
import { useAuth } from '@/hooks/useAuth';
import { VerifyToContinueButton } from '@/features/verification/CompanyVerificationGate';
import { useCompanyVerification } from '@/features/verification/useCompanyVerification';
import { usePermissions } from '@/hooks/usePermissions';
import { paths } from '@/routes/paths';
import { COMPANY_ROLES, PERMISSIONS } from '@/types/roles';
import { cn } from '@/utils/cn';
import { formatDate } from '@/utils/format';
import { memberInviteSchema } from '@/validations';

const STATUS_TONES = { ACTIVE: 'success', INVITED: 'warning', REMOVED: 'neutral' };

export function CompanyTeamPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [permissionsFor, setPermissionsFor] = useState(null);
  // The invite result, kept after the form closes: the temporary password is
  // only ever returned once, so it must stay on screen until it is copied.
  const [invited, setInvited] = useState(null);
  const { user } = useAuth();
  const { can } = usePermissions();
  // The API refuses these anyway; hiding them saves a member a dead end.
  const canManage = can(PERMISSIONS.COMPANY_MEMBERS_MANAGE);
  // Inviting and changing access wait for verification; the API refuses them.
  const { needsVerification } = useCompanyVerification();
  const inviteButton = needsVerification ? (
    <VerifyToContinueButton />
  ) : (
    <Button onClick={openInvite}>
      <Icon name="plus" size={16} />
      Add team member
    </Button>
  );

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['company-members', page],
    queryFn: () => companyApi.listMembers({ page, page_size: 20 }),
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(memberInviteSchema),
    defaultValues: { role: 'RECRUITER' },
  });
  const selectedRole = watch('role');

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['company-members'] });
  }

  const invite = useMutation({
    mutationFn: (values) => companyApi.inviteMember(values),
    onSuccess: (result) => {
      invalidate();
      setInviteOpen(false);
      reset({ role: 'RECRUITER' });
      if (result.invite_debug_token) setInvited(result);
      else toast.success(`Invitation sent to ${result.member.email}`);
    },
  });

  // A full plan is not something the form can fix, so it is shown inside the
  // dialog with the way out rather than as a toast that vanishes behind it.
  const seatLimit = isPlanLimitError(invite.error) ? invite.error : null;

  function openInvite() {
    invite.reset();
    setInviteOpen(true);
  }

  function closeInvite() {
    invite.reset();
    setInviteOpen(false);
  }

  const changeRole = useMutation({
    mutationFn: ({ id, role }) => companyApi.updateMemberRole(id, { role }),
    onSuccess: () => {
      toast.success('Role updated');
      invalidate();
    },
    onError: (roleError) => toast.error(errorMessage(roleError, 'Could not change the role')),
  });

  const remove = useMutation({
    mutationFn: (id) => companyApi.removeMember(id),
    onSuccess: () => {
      toast.success('Member removed');
      invalidate();
      setPendingRemoval(null);
    },
    onError: (removeError) => {
      toast.error(errorMessage(removeError, 'Could not remove the member'));
      setPendingRemoval(null);
    },
  });

  if (isPending) return <LoadingState label="Loading your team…" />;
  if (isError) {
    return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  }

  const members = data?.data ?? [];
  const meta = data?.meta;

  async function onInvite(values) {
    const payload = Object.fromEntries(
      Object.entries(values).filter(([, value]) => value !== '' && value !== undefined),
    );
    try {
      await invite.mutateAsync(payload);
    } catch (mutationError) {
      if (isPlanLimitError(mutationError)) return;
      if (!applyFieldErrors(mutationError, setError)) {
        toast.error(errorMessage(mutationError, 'Could not send the invitation'));
      }
    }
  }

  return (
    <div>
      <PageHeader
        title="Team"
        description="Who can access this workspace, and what they can do."
        action={
          canManage && (
            <div className="flex flex-wrap gap-2">
              <LinkButton to={paths.company.roles} variant="secondary">
                <Icon name="shieldCheck" size={16} />
                Roles and permissions
              </LinkButton>
              {inviteButton}
            </div>
          )
        }
      />

      <div className="grid items-start gap-6 lg:grid-cols-[1fr_320px]">
        <ListCard
          state={
            members.length === 0 ? (
              <EmptyState
                icon={<Icon name="users" size={22} />}
                title="No team members yet"
                description="Invite a colleague to help manage internships and candidates."
                action={canManage && inviteButton}
              />
            ) : null
          }
          footer={
            meta &&
            meta.total_pages > 1 && (
              <Pagination
                className=""
                page={meta.page}
                totalPages={meta.total_pages}
                totalItems={meta.total_items}
                onPageChange={setPage}
              />
            )
          }
        >
          <BoxList>
            {members.map((member) => (
              <BoxRow
                key={member.id}
                accent={STATUS_TONES[member.status] ?? 'neutral'}
                leading={<BoxAvatar name={member.full_name} round />}
                title={member.full_name}
                badges={
                  <>
                    <Badge dot tone={STATUS_TONES[member.status] ?? 'neutral'}>
                      {statusLabel(member.status)}
                    </Badge>
                    {member.is_owner && <Badge tone="brand">Owner</Badge>}
                    {member.has_custom_permissions && (
                      <Badge tone="info" title="Given or denied permissions beyond the role">
                        Custom permissions
                      </Badge>
                    )}
                  </>
                }
                subtitle={member.email}
                facts={[
                  member.job_title && { icon: 'idBadge', text: member.job_title },
                  // The owner's role is fixed, so it is a fact rather than a
                  // control -- as is everyone's for a member who cannot manage.
                  (member.is_owner || !canManage) && {
                    icon: 'userCog',
                    text: roleLabel(member.role),
                  },
                ]}
                meta={
                  member.joined_at && (
                    <span className="text-ink-muted text-xs">
                      Joined {formatDate(member.joined_at)}
                    </span>
                  )
                }
                actions={
                  canManage &&
                  !member.is_owner && (
                    <CardActions>
                      {member.role !== COMPANY_ROLES.COMPANY_ADMIN &&
                        member.user_id !== user?.id && (
                          <Button
                            variant="secondary"
                            size="sm"
                            title={`Permissions for ${member.full_name}`}
                            onClick={() => setPermissionsFor(member)}
                          >
                            <Icon name="shieldCheck" size={14} />
                            Permissions
                          </Button>
                        )}
                      <Select
                        label={`Role for ${member.full_name}`}
                        hideLabel
                        size="sm"
                        wrapperClassName="w-40"
                        value={member.role}
                        disabled={changeRole.isPending}
                        onChange={(event) =>
                          changeRole.mutate({ id: member.id, role: event.target.value })
                        }
                      >
                        {ROLES.map((role) => (
                          <option key={role.value} value={role.value}>
                            {role.label}
                          </option>
                        ))}
                      </Select>
                    </CardActions>
                  )
                }
                // Removing someone ends their membership: top right, apart.
                corner={
                  canManage &&
                  !member.is_owner && (
                    <Button
                      variant="ghostDanger"
                      size="sm"
                      title={`Remove ${member.full_name}`}
                      onClick={() => setPendingRemoval(member)}
                    >
                      <Icon name="trash" size={14} />
                      Remove
                    </Button>
                  )
                }
              />
            ))}
          </BoxList>
        </ListCard>

        <Card>
          <CardHeader
            title="Roles"
            description="Defaults shown. Each company can change what a role grants."
          />
          <ul className="space-y-4">
            {ROLES.map((role) => (
              <li key={role.value} className="flex gap-3">
                <span
                  aria-hidden="true"
                  className="bg-brand-50 text-brand-600 grid size-9 shrink-0 place-items-center rounded-lg"
                >
                  <Icon name="users" size={17} />
                </span>
                <div className="min-w-0">
                  <p className="text-ink text-sm font-semibold">{role.label}</p>
                  <p className="text-ink-muted mt-0.5 text-sm">{role.hint}</p>
                </div>
              </li>
            ))}
          </ul>
          {canManage && (
            <LinkButton to={paths.company.roles} variant="secondary" className="mt-5 w-full">
              Customise roles
            </LinkButton>
          )}
        </Card>
      </div>

      <Modal
        open={Boolean(invited)}
        title="Invitation sent"
        description={invited ? `${invited.member.full_name} (${invited.member.email})` : undefined}
        onClose={() => setInvited(null)}
      >
        {invited && (
          <div className="space-y-4">
            <p className="text-ink-muted text-sm">
              They sign in with their email address and this temporary password, then change it from
              account settings. It is also in the invitation email.
            </p>
            <div className="border-line bg-surface-muted flex items-center gap-2 rounded-lg border p-3">
              <code className="text-ink min-w-0 flex-1 font-mono text-base tracking-wider break-all">
                {invited.invite_debug_token}
              </code>
              <Button
                size="sm"
                variant="secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(invited.invite_debug_token);
                    toast.success('Password copied');
                  } catch {
                    toast.error('Could not copy. Select the password and copy it instead.');
                  }
                }}
              >
                <Icon name="clipboard" size={15} />
                Copy
              </Button>
            </div>
            <Alert tone="warning">
              This is the only time the password is shown. If it is lost, remove the member and
              invite them again.
            </Alert>
            <div className="flex justify-end">
              <Button onClick={() => setInvited(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>

      <MemberPermissionsModal member={permissionsFor} onClose={() => setPermissionsFor(null)} />

      <Modal
        open={inviteOpen}
        title="Add a team member"
        description="They receive an email with sign-in instructions."
        onClose={closeInvite}
      >
        <form onSubmit={handleSubmit(onInvite)} className="space-y-4" noValidate>
          {seatLimit && (
            <Alert tone="warning" title={seatLimit.message}>
              <p>
                {seatLimit.errors.find((item) => item.field === 'plan')?.message ??
                  'Upgrade your plan to add more team members.'}
              </p>
              <LinkButton to={paths.company.billing} size="sm" className="mt-3">
                Upgrade plan
              </LinkButton>
            </Alert>
          )}
          <Input
            label="Work email"
            type="email"
            required
            error={errors.email?.message}
            {...register('email')}
          />
          <Input
            label="Full name"
            hint="Optional. Used in the invitation email."
            error={errors.full_name?.message}
            {...register('full_name')}
          />
          <fieldset>
            <legend className="text-ink mb-2 text-[12px] font-medium">
              Role <span className="text-danger-600">*</span>
            </legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {ROLES.map((role) => {
                const active = role.value === selectedRole;
                return (
                  <label
                    key={role.value}
                    className={cn(
                      'rounded-input flex cursor-pointer items-start gap-2.5 border px-3 py-2.5 transition',
                      active
                        ? 'border-brand-500 bg-brand-50/70 ring-brand-100 ring-2'
                        : 'border-line hover:border-brand-300 bg-surface',
                    )}
                  >
                    <input
                      type="radio"
                      value={role.value}
                      className="accent-brand-600 mt-0.5"
                      {...register('role')}
                    />
                    <span className="min-w-0">
                      <span className="text-ink block text-sm font-medium">{role.label}</span>
                      <span className="text-ink-muted block text-xs">{role.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
            {errors.role?.message && (
              <p className="text-danger-600 mt-1.5 text-xs">{errors.role.message}</p>
            )}
          </fieldset>
          <Input label="Job title" error={errors.job_title?.message} {...register('job_title')} />

          <div className="flex justify-end gap-2">
            <Button variant="secondary" type="button" onClick={closeInvite}>
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isSubmitting || invite.isPending}
              disabled={Boolean(seatLimit)}
            >
              Send invitation
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remove team member"
        message={`${pendingRemoval?.full_name} will lose access to this workspace immediately.`}
        confirmLabel="Remove"
        isLoading={remove.isPending}
        onConfirm={() => remove.mutate(pendingRemoval.id)}
        onCancel={() => setPendingRemoval(null)}
      />
    </div>
  );
}
